/* eslint-disable import/no-extraneous-dependencies */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { INfse, INfseStats } from '../hooks/useNfse';
import {
  formatDocumento,
  getIssApurado,
  getIssProprio,
  getRetPisCofinsLabel,
  isIssRetido,
} from './nfseCampos';

// Cache da logo comprimida
let cachedLogoDataUrl: string | null = null;

const getCompressedLogo = (): Promise<string> => {
  if (cachedLogoDataUrl) return Promise.resolve(cachedLogoDataUrl);

  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 256;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      cachedLogoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      resolve(cachedLogoDataUrl);
    };
    img.onerror = () => resolve('');
    img.src = `${window.location.origin}/logo-especialistas.png`;
  });
};

const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

export interface PdfColumnDef {
  key: string;
  label: string;
  // eslint-disable-next-line no-unused-vars
  getValue: (nota: INfse) => string;
  align?: 'left' | 'center' | 'right';
  width?: number;
}

export const ALL_PDF_COLUMNS: PdfColumnDef[] = [
  {
    key: 'data_emissao',
    label: 'Data Nota',
    getValue: n => formatDate(n.data_emissao),
    align: 'center',
    width: 22,
  },
  {
    key: 'data_competencia',
    label: 'Competência',
    getValue: n => formatDate(n.data_competencia),
    align: 'center',
    width: 22,
  },
  {
    key: 'numero_nfse',
    label: 'Nº NF',
    getValue: n => n.numero_nfse || '-',
    align: 'center',
    width: 18,
  },
  {
    key: 'emissor_nome',
    label: 'Nome Emitente',
    getValue: n => n.emissor_nome || '-',
    align: 'left',
    width: 40,
  },
  {
    key: 'emissor_cnpj',
    label: 'CNPJ Emitente',
    getValue: n => {
      const docNum = n.emissor_cnpj || n.emissor_cpf;
      return docNum ? formatDocumento(docNum) : '-';
    },
    align: 'center',
    width: 32,
  },
  {
    key: 'local_emissao',
    label: 'Cidade Emitente',
    getValue: n => n.local_emissao || '-',
    align: 'left',
    width: 30,
  },
  {
    key: 'tomador_nome',
    label: 'Nome Tomador',
    getValue: n => n.tomador_nome || '-',
    align: 'left',
    width: 40,
  },
  {
    key: 'tomador_cnpj',
    label: 'CNPJ Tomador',
    getValue: n => {
      const docNum = n.tomador_cnpj || n.tomador_cpf;
      return docNum ? formatDocumento(docNum) : '-';
    },
    align: 'center',
    width: 32,
  },
  {
    key: 'tomador_municipio',
    label: 'Cidade Tomador',
    getValue: n => {
      if (n.tomador_nome_municipio) {
        return `${n.tomador_nome_municipio}${n.tomador_uf ? ` - ${n.tomador_uf}` : ''}`;
      }
      return '-';
    },
    align: 'left',
    width: 30,
  },
  {
    key: 'local_incidencia',
    label: 'M. Incidência',
    getValue: n => n.local_incidencia || '-',
    align: 'left',
    width: 28,
  },
  {
    key: 'tipo',
    label: 'Tipo',
    getValue: n => (n.tipo === 'prestado' ? 'Prestada' : 'Tomada'),
    align: 'center',
    width: 18,
  },
  {
    key: 'valor_serv_prest',
    label: 'Vr. Contábil',
    getValue: n => formatCurrency(n.valor_serv_prest || 0),
    align: 'right',
    width: 25,
  },
  {
    key: 'valor_bc',
    label: 'Base Cálculo',
    getValue: n => formatCurrency(n.valor_bc || 0),
    align: 'right',
    width: 25,
  },
  {
    key: 'aliquota_aplic',
    label: 'Aliq ISS',
    getValue: n => (n.aliquota_aplic ? `${n.aliquota_aplic}%` : '-'),
    align: 'center',
    width: 16,
  },
  {
    // ISS exclusivo (regra compartilhada em nfseCampos): retido vai só em
    // "ISS Ret" — somar as colunas não dobra o ISS
    key: 'valor_issqn',
    label: 'ISS Normal',
    getValue: n => (getIssProprio(n) ? formatCurrency(getIssProprio(n)) : '-'),
    align: 'right',
    width: 22,
  },
  {
    key: 'iss_retido',
    label: 'ISS Ret',
    getValue: n => (isIssRetido(n) ? formatCurrency(getIssApurado(n)) : '-'),
    align: 'right',
    width: 22,
  },
  {
    // Fallback pros campos legados (mesma regra do DANFSe)
    key: 'irrf',
    label: 'IRRF',
    getValue: n => (n.v_ret_irrf || n.irrf ? formatCurrency(n.v_ret_irrf || n.irrf || 0) : '-'),
    align: 'right',
    width: 22,
  },
  {
    // Débito de apuração própria (NT-008 §2.1.9): sempre exibido;
    // a retenção é o indicador da coluna Ret. PIS/COF
    key: 'pis',
    label: 'PIS',
    getValue: n => (n.v_pis || n.pis ? formatCurrency(n.v_pis || n.pis || 0) : '-'),
    align: 'right',
    width: 22,
  },
  {
    key: 'cofins',
    label: 'COFINS',
    getValue: n => (n.v_cofins || n.cofins ? formatCurrency(n.v_cofins || n.cofins || 0) : '-'),
    align: 'right',
    width: 22,
  },
  {
    // Indicador do leiaute (regra compartilhada em nfseCampos)
    key: 'tp_ret_pis_cofins',
    label: 'Ret. PIS/COF',
    getValue: n => getRetPisCofinsLabel(n.tp_ret_pis_cofins),
    align: 'center',
    width: 22,
  },
  {
    key: 'csll',
    label: 'CSLL',
    getValue: n => (n.v_ret_csll || n.csll ? formatCurrency(n.v_ret_csll || n.csll || 0) : '-'),
    align: 'right',
    width: 22,
  },
  {
    key: 'inss',
    label: 'INSS',
    getValue: n => (n.v_ret_inss || n.inss ? formatCurrency(n.v_ret_inss || n.inss || 0) : '-'),
    align: 'right',
    width: 22,
  },
  {
    // servico_codigo é o Código de Tributação Nacional (não CNAE) — NT-008
    key: 'servico_codigo',
    label: 'Cód. Trib. Nacional',
    getValue: n => n.servico_codigo || '-',
    align: 'center',
    width: 18,
  },
  {
    key: 'tributo_nacional',
    label: 'Desc. Trib. Nacional',
    getValue: n => {
      const desc = n.tributo_nacional || '-';
      return desc.length > 60 ? `${desc.substring(0, 57)}...` : desc;
    },
    align: 'left',
    width: 40,
  },
  {
    key: 'chave_nfse',
    label: 'Chave NFS-e',
    getValue: n => n.chave_nfse || '-',
    align: 'left',
    width: 35,
  },
  {
    key: 'emissor_im',
    label: 'IM Emitente',
    getValue: n => n.emissor_im || n.prestador_im || '-',
    align: 'center',
    width: 22,
  },
  {
    key: 'discriminacao',
    label: 'Discriminação',
    getValue: n => {
      const desc = n.servico_descricao || '-';
      return desc.length > 80 ? `${desc.substring(0, 77)}...` : desc;
    },
    align: 'left',
    width: 50,
  },
  {
    key: 'servico_codigo_trib_mun',
    label: 'Cód Trib. Mun.',
    getValue: n => n.servico_codigo_trib_mun || '-',
    align: 'center',
    width: 22,
  },
  {
    // local_incidencia é o município de incidência do ISSQN, não descrição de código
    key: 'desc_trib_municipio',
    label: 'Município Incidência',
    getValue: n => n.local_incidencia || '-',
    align: 'left',
    width: 30,
  },
];

// Colunas padrão pré-selecionadas
export const DEFAULT_SELECTED_COLUMNS = [
  'data_emissao',
  'numero_nfse',
  'emissor_nome',
  'emissor_cnpj',
  'tomador_nome',
  'tomador_cnpj',
  'tipo',
  'valor_serv_prest',
  'valor_bc',
  'aliquota_aplic',
  'valor_issqn',
  'iss_retido',
  'irrf',
  'pis',
  'cofins',
  'csll',
  'inss',
];

interface ExportPdfOptions {
  notas: INfse[];
  stats: INfseStats | undefined;
  selectedColumns: string[];
  companyName: string;
  companyCnpj: string;
  companyId: string;
  periodo: string;
  exportMode?: 'unificado' | 'separado';
}

async function generateSinglePdf(
  notas: INfse[],
  stats: INfseStats | undefined,
  columns: PdfColumnDef[],
  companyName: string,
  companyCnpj: string,
  companyId: string,
  periodo: string,
  fileName: string,
  subtitle?: string,
) {
  if (columns.length === 0 || notas.length === 0) return;

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;

  // === HEADER COM LOGO ===
  const logoWidth = 20;
  const logoHeight = 20;
  const logoData = await getCompressedLogo();
  if (logoData) {
    doc.addImage(logoData, 'JPEG', margin, 4, logoWidth, logoHeight);
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const title = subtitle ? `Relatório de NFS-e - ${subtitle}` : 'Relatório de NFS-e';
  doc.text(title, pageWidth / 2, 12, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${companyName}`, pageWidth / 2, 18, { align: 'center' });
  doc.text(`CNPJ: ${companyCnpj} | Cód: ${companyId} | Período: ${periodo}`, pageWidth / 2, 23, {
    align: 'center',
  });

  // === KPIs ===
  let yPos = 30;

  if (stats) {
    const kpis = [
      { label: 'Total de Notas', value: String(stats.total || 0) },
      { label: 'Prestadas', value: String(stats.total_prestados || 0) },
      { label: 'Tomadas', value: String(stats.total_tomados || 0) },
      {
        label: 'Ativas',
        value: String(
          stats.total_ativas ||
            (stats.total || 0) - (stats.total_canceladas || 0) - (stats.total_substituidas || 0),
        ),
      },
      { label: 'Canceladas', value: String(stats.total_canceladas || 0) },
      { label: 'Substituídas', value: String(stats.total_substituidas || 0) },
      { label: 'Vr. Prestadas', value: formatCurrency(stats.valor_total_prestados || 0) },
      { label: 'Vr. Tomadas', value: formatCurrency(stats.valor_total_tomados || 0) },
      { label: 'Ret. Federal', value: String(stats.total_com_retencao_federal || 0) },
      { label: 'Ret. Municipal', value: String(stats.total_com_retencao_municipal || 0) },
      { label: 'Sem Retenção', value: String(stats.total_sem_retencao || 0) },
    ];

    // 6 por linha: contagens na 1ª linha, valores e retenções na 2ª
    const kpiCols = 6;
    const kpiColWidth = (pageWidth - margin * 2) / kpiCols;
    const kpiRowHeight = 12;

    // Fundo cinza claro para KPIs
    const totalKpiRows = Math.ceil(kpis.length / kpiCols);
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(
      margin,
      yPos - 2,
      pageWidth - margin * 2,
      totalKpiRows * kpiRowHeight + 4,
      2,
      2,
      'F',
    );

    kpis.forEach((kpi, i) => {
      const col = i % kpiCols;
      const row = Math.floor(i / kpiCols);
      const x = margin + col * kpiColWidth + kpiColWidth / 2;
      const y = yPos + row * kpiRowHeight;

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(kpi.label, x, y + 3, { align: 'center' });

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(kpi.value, x, y + 8, { align: 'center' });
    });

    yPos += totalKpiRows * kpiRowHeight + 8;
    doc.setTextColor(0, 0, 0);
  }

  // === TABELA ===
  const headers = columns.map(col => col.label);
  const body = notas.map(nota => columns.map(col => col.getValue(nota)));

  // Calcula larguras proporcionais
  const totalWidth = pageWidth - margin * 2;
  const totalWeight = columns.reduce((sum, col) => sum + (col.width || 25), 0);
  const colWidths = columns.map(col => ((col.width || 25) / totalWeight) * totalWidth);

  autoTable(doc, {
    startY: yPos,
    head: [headers],
    body,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 6,
      cellPadding: 1.5,
      overflow: 'linebreak',
      lineWidth: 0.1,
      lineColor: [200, 200, 200],
    },
    headStyles: {
      fillColor: [49, 130, 206],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 6.5,
      halign: 'center',
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: columns.reduce(
      (acc, col, i) => {
        acc[i] = {
          halign: col.align || 'left',
          cellWidth: colWidths[i],
        };
        return acc;
      },
      {} as Record<number, any>,
    ),
    didDrawPage: (data: any) => {
      // Rodapé com paginação
      const pageCount = doc.getNumberOfPages();
      const currentPage = data.pageNumber;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Página ${currentPage} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'center' },
      );
      doc.text(
        `Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`,
        pageWidth - margin,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'right' },
      );
    },
  });

  doc.save(fileName);
}

export async function exportNfseTablePdf({
  notas,
  stats,
  selectedColumns,
  companyName,
  companyCnpj,
  companyId,
  periodo,
  exportMode = 'unificado',
}: ExportPdfOptions) {
  const columns = ALL_PDF_COLUMNS.filter(col => selectedColumns.includes(col.key));

  if (columns.length === 0 || notas.length === 0) return;

  const periodoFile = periodo.replace(/\s/g, '_');

  if (exportMode === 'separado') {
    const tomadas = notas.filter(n => n.tipo === 'tomado');
    const prestadas = notas.filter(n => n.tipo === 'prestado');

    if (tomadas.length > 0) {
      await generateSinglePdf(
        tomadas,
        stats,
        columns,
        companyName,
        companyCnpj,
        companyId,
        periodo,
        `NFS-e_${companyId}_${periodoFile}_Tomadas.pdf`,
        'Tomadas',
      );
    }

    if (prestadas.length > 0) {
      await generateSinglePdf(
        prestadas,
        stats,
        columns,
        companyName,
        companyCnpj,
        companyId,
        periodo,
        `NFS-e_${companyId}_${periodoFile}_Prestadas.pdf`,
        'Prestadas',
      );
    }
  } else {
    await generateSinglePdf(
      notas,
      stats,
      columns,
      companyName,
      companyCnpj,
      companyId,
      periodo,
      `NFS-e_${companyId}_${periodoFile}.pdf`,
    );
  }
}
